import type { ReactNode } from 'react';

/**
 * Paints named phrases inside a sentence. The sentence stays one string with the
 * phrases listed beside it, so a meta description or a search index reads the
 * same string. `icons` is a second set of phrases, each with a glyph in front of
 * it, and a phrase in both sets takes the glyph and the colour.
 *
 * The split uses a capturing group, so the separators come back in the array and
 * the sentence reassembles in order. Phrases are escaped because they are copy
 * and may one day hold a regex character.
 */
export const highlight = (
  text: string,
  phrases: ReadonlyArray<string>,
  className: string,
  icons?: Record<string, ReactNode>,
): ReactNode => {
  const all = [...new Set([...phrases, ...Object.keys(icons ?? {})])];
  if (!all.length) return text;

  /* Longest first. A regex alternation takes the first branch that matches, so
     "giving" before "giving layer" would match the short one and leave " layer"
     as plain text. */
  const pattern = [...all]
    .sort((a, b) => b.length - a.length)
    .map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|');
  return text.split(new RegExp(`(${pattern})`)).map((part, i) => {
    if (!all.includes(part)) return part;

    const icon = icons?.[part];
    const paint = phrases.includes(part) ? className : undefined;
    if (icon === undefined) {
      return (
        <span key={i} className={paint}>
          {part}
        </span>
      );
    }

    const [first, ...rest] = part.split(' ');
    return (
      /* The glyph and the first word travel together, so a wrap cannot leave an
         icon at the end of one line and its phrase at the start of the next. The
         rest of the phrase stays breakable.

         The gap is in em, and every glyph here has its box cropped to its own
         ink, which is what makes one number work on all of them. */
      <span key={i} className={paint}>
        <span className='inline-flex items-baseline gap-[0.25em] whitespace-nowrap'>
          {icon}
          {first}
        </span>
        {rest.length ? ` ${rest.join(' ')}` : null}
      </span>
    );
  });
};
